import React, { useCallback } from 'react';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import type { EntryPointReferenceSubject } from '@atlassian/jira-entry-point';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container';
import { useIntl } from '@atlassian/jira-intl';

// eslint-disable-next-line jira/import/no-restricted-paths
import { useIssueCreateActions } from '../../../controllers/issue-create-form';
import { PACKAGE_NAME, TEAM_NAME } from '../../constants';
import { ContextFieldsFallback } from './context-fields';
import type ContextFieldsEntryPoint from './context-fields/entrypoint';
import messages from './messages';
import Summary from './summary';

interface Props {
	/**
	 * Entry point reference subject for context fields in the issue create form, i.e. issue type. This allows
	 * preloading of project and issue type data on user intent to open the form. The parent is responsible for ensuring
	 * an appropriate trigger is defined to load this entry point.
	 */
	contextFieldsSubject: EntryPointReferenceSubject<typeof ContextFieldsEntryPoint>;
}
const runtimeProps = {};

const IssueCreateForm = ({ contextFieldsSubject }: Props) => {
	const { formatMessage } = useIntl();

	const { resetSummary } = useIssueCreateActions();

	const onSubmit = useCallback(() => {
		// TODO EM-6706 Commit mutation

		resetSummary();
	}, [resetSummary]);

	return (
		<Box
			as="form"
			paddingInlineStart="space.300"
			paddingInlineEnd="space.100"
			xcss={containerStyles}
		>
			<JiraEntryPointContainer
				entryPointReferenceSubject={contextFieldsSubject}
				id="async-issue-create-context-fields"
				packageName={PACKAGE_NAME}
				teamName={TEAM_NAME}
				fallback={<ContextFieldsFallback />}
				runtimeProps={runtimeProps}
			/>
			<Summary />
			<Box xcss={actionStyles}>
				<Button onClick={onSubmit}>{formatMessage(messages.createButton)}</Button>
			</Box>
		</Box>
	);
};

const containerStyles = xcss({
	alignItems: 'center',
	display: 'flex',
	gap: 'space.200',
});

const actionStyles = xcss({
	flexShrink: 0,
	marginRight: 'space.0',
});

export default IssueCreateForm;
